import { useEffect, useState, lazy, Suspense } from "react";
import './App.css';
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import LanguageNudge from "./Components/LanguageNudge.jsx";
import { useDispatch } from 'react-redux';
import ScrollToTop from "./Components/Helper/ScrollToTop.jsx";
import SessionExpiryListener from "./Components/Auth/SessionExpiryListener.jsx";
import { setUserFromLocalStorage } from '../src/Store/userSlice';
import { setTenantFromLocalStorage } from '../src/Store/tenantSlice';
import { setStudentFromLocalStorage } from "./Store/studentAuthSlice";
import GenerationStatusBannerHost from "./Components/GenerationStatusBannerHost.jsx";
import LoginCheck from "./Pages/Dashboard/LoginCheck";
import SuperAdminCheck from "./Pages/Admin/SuperAdminCheck.jsx";
import OwnerCheck from "./Pages/Admin/OwnerCheck.jsx";
import InstituteAdminCheck from "./Pages/Admin/InstituteAdminCheck.jsx";
import StudentLoginCheck from "./Components/Student/StudentLoginCheck.jsx";
import { getCurrentTrackSlug } from "./utils/trackResolver.js";
import { getLang, getStoredLang, setLang, isNudgeSeen, resolveLangFromUrl } from "./i18n/lang.js";

// Marketing chunk — Homepage is the landing route; lazy-loading it keeps
// teacher-app code (MUI, redux extras pulled in via descendants) out of the
// entry chunk for users who never visit '/'.
const Homepage = lazy(() => import(/* webpackChunkName: "marketing" */ "./Pages/Homepage/Homepage"));
const RequestDemo = lazy(() => import(/* webpackChunkName: "marketing" */ "./Pages/Homepage/RequestDemo"));
const CompanyPage = lazy(() => import(/* webpackChunkName: "marketing" */ "./Pages/Company/CompanyPage"));
const PrivacyPolicyPage = lazy(() => import(/* webpackChunkName: "marketing" */ "./Pages/Legal/PrivacyPolicyPage"));

// Auth chunk (teachers + shared)
const Auth = lazy(() => import(/* webpackChunkName: "auth" */ "./Pages/Authpage/Auth"));
const TeacherRegister = lazy(() => import(/* webpackChunkName: "auth" */ "./Pages/Authpage/TeacherRegister"));
const TeacherLogin = lazy(() => import(/* webpackChunkName: "auth" */ "./Pages/Authpage/TeacherLogin"));
const RequestAccess = lazy(() => import(/* webpackChunkName: "auth" */ "./Pages/Authpage/RequestAccess"));
const TeacherLoginPage = lazy(() => import(/* webpackChunkName: "auth" */ "./Pages/Authpage/TeacherLoginPage"));
const IndependentLoginPage = lazy(() => import(/* webpackChunkName: "auth" */ "./Pages/Authpage/IndependentLoginPage"));
const Forgotpassword = lazy(() => import(/* webpackChunkName: "auth" */ "./Pages/Authpage/Forgotpassword"));
const ResetPassword = lazy(() => import(/* webpackChunkName: "auth" */ "./Pages/Authpage/ResetPassword"));
const ForcePasswordChange = lazy(() => import(/* webpackChunkName: "auth" */ "./Pages/Authpage/ForcePasswordChange"));
const AcceptInvite = lazy(() => import(/* webpackChunkName: "auth" */ "./Pages/Authpage/AcceptInvite.jsx"));
const AuthHeader = lazy(() => import(/* webpackChunkName: "auth" */ "./Pages/Authpage/AuthHeader.jsx"));

// Teacher app chunk — assessment authoring + dashboard
const MyProfileLanding = lazy(() => import(/* webpackChunkName: "teacher-app" */ "./Pages/Dashboard/MyProfileLanding"));
const TeacherProfile = lazy(() => import(/* webpackChunkName: "teacher-app" */ "./Pages/Profile/TeacherProfile.jsx"));
const CreateAssessment = lazy(() => import(/* webpackChunkName: "teacher-app" */ "./Pages/Assessment/CreateAssessment.jsx"));
const UploadAssessment = lazy(() => import(/* webpackChunkName: "teacher-app" */ "./Pages/Assessment/UploadAssessment.jsx"));
const SearchFilter = lazy(() => import(/* webpackChunkName: "teacher-app" */ "./Pages/Assessment/SearchFilter.jsx"));
const ViewAssessments = lazy(() => import(/* webpackChunkName: "teacher-app" */ "./Pages/Assessment/ViewAssessments.jsx"));
const ConfirmQuestions = lazy(() => import(/* webpackChunkName: "teacher-app" */ "./Pages/Assessment/ConfirmQuestions.jsx"));
const ConfirmAnswers = lazy(() => import(/* webpackChunkName: "teacher-app" */ "./Pages/Assessment/ConfirmAnswers.jsx"));
const ConfirmAnswersheet = lazy(() => import(/* webpackChunkName: "teacher-app" */ "./Pages/Assessment/ConfirmAnswersheet.jsx"));
const ViewAnswers = lazy(() => import(/* webpackChunkName: "teacher-app" */ "./Pages/Assessment/ViewAnswers.jsx"));
const RubricManager = lazy(() => import(/* webpackChunkName: "teacher-app" */ "./Pages/Assessment/RubricManager.jsx"));

// Evaluation chunk
const EvaluateAssessment = lazy(() => import(/* webpackChunkName: "evaluation" */ "./Pages/Assessment/EvaluateAssessment.jsx"));
const ViewEvaluations = lazy(() => import(/* webpackChunkName: "evaluation" */ "./Pages/Assessment/ViewEvaluations.jsx"));
const QuestionBankAnalyticsPage = lazy(() => import(/* webpackChunkName: "evaluation" */ "./Pages/Analytics/QuestionBankAnalyticsPage.jsx"));
const EvaluationReport = lazy(() => import(/* webpackChunkName: "evaluation" */ "./Pages/Assessment/EvaluationReport.jsx"));
const TeacherReview = lazy(() => import(/* webpackChunkName: "evaluation" */ "./Pages/Assessment/TeacherReview.jsx"));

// Scan chunk (heavy: image processing)
const RapidCapture = lazy(() => import(/* webpackChunkName: "scan" */ "./Pages/Assessment/RapidCapture.jsx"));
const ScanEvaluate = lazy(() => import(/* webpackChunkName: "scan" */ "./Pages/Assessment/ScanEvaluate.jsx"));
const TakeAttendance = lazy(() => import(/* webpackChunkName: "attendance" */ "./Pages/Attendance/TakeAttendance.jsx"));
const ReviewQueue = lazy(() => import(/* webpackChunkName: "scan" */ "./Pages/Assessment/ReviewQueue.jsx"));

// Online assessments / quiz chunk (public + teacher dashboard)
const CreateQuiz = lazy(() => import(/* webpackChunkName: "online" */ "./Pages/Quiz/Teacher/CreateQuiz.jsx"));
const QuizFinalized = lazy(() => import(/* webpackChunkName: "online" */ "./Pages/Quiz/Teacher/QuizFinalized.jsx"));
const ViewResult = lazy(() => import(/* webpackChunkName: "online" */ "./Pages/OnlineMockTest/ViewResult.jsx"));
const OnlineAssessmentsDashboard = lazy(() => import(/* webpackChunkName: "online" */ "./Pages/OnlineAssessment/TeacherDashboard/OnlineAssessmentsDashboard.jsx"));
const AssessmentAnalyticsPage = lazy(() => import(/* webpackChunkName: "online" */ "./Pages/OnlineAssessment/TeacherDashboard/AssessmentAnalyticsPage.jsx"));
const ParticipantEntryPage = lazy(() => import(/* webpackChunkName: "online" */ "./Pages/OnlineAssessment/ParticipantFlow/ParticipantEntryPage.jsx"));
const ParticipantQuizPage = lazy(() => import(/* webpackChunkName: "online" */ "./Pages/OnlineAssessment/ParticipantFlow/ParticipantQuizPage.jsx"));
const QuizResultPage = lazy(() => import(/* webpackChunkName: "online" */ "./Pages/OnlineAssessment/ParticipantFlow/QuizResultPage.jsx"));

// Admin chunk
const AdminAccess = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/AdminAccess.jsx"));
const StudentRegistrationsQueue = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/StudentRegistrationsQueue.jsx"));
const TenantManagement = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/TenantManagement.jsx"));
const LeadsManagement = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/LeadsManagement.jsx"));
const EvaluationOverview = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/EvaluationOverview.jsx"));
const AdminQuestionPaperView = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/AdminQuestionPaperView.jsx"));
const AdminAnswerSheetView = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/AdminAnswerSheetView.jsx"));
const TeacherManagement = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/TeacherManagement.jsx"));
const ClassWiseStudentDashboard = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/ClassWiseStudentDashboard.jsx"));
const FaceEnrollment = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/FaceEnrollment.jsx"));
const ProfileChangeRequests = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/ProfileChangeRequests.jsx"));
// College course lists (2026-10-21, PRD-COURSE-SCREENS slot 6).
const CourseCatalogue = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/CourseCatalogue.jsx"));
const DiagramGapsDashboard = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/DiagramGapsDashboard.jsx"));
const LogViewer = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/LogViewer.jsx"));
const ExtractionLab = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/ExtractionLab.jsx"));
const ModelMappings = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/ModelMappings.jsx"));
const PromptManager = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/PromptManager/PromptManager.jsx"));
const PromptCommitsPage = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/PromptManager/CommitsPage.jsx"));
const SuggestedTopicsQueue = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Admin/SuggestedTopics/SuggestedTopicsQueue.jsx"));
const PrincipalDashboard = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Principal/PrincipalDashboard.jsx"));
const TeacherDrilldown = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Principal/TeacherDrilldown.jsx"));
const TeacherDetailView = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Principal/TeacherDetailView.jsx"));
const ClassPerformance = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Principal/Analytics/ClassPerformance.jsx"));
const SubjectBreakdown = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Principal/Analytics/SubjectBreakdown.jsx"));
const TeacherActivity = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Principal/Analytics/TeacherActivity.jsx"));
const StudentsAnalytics = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Principal/Analytics/StudentsAnalytics.jsx"));
const StudentAnalyticsDetail = lazy(() => import(/* webpackChunkName: "admin" */ "./Pages/Principal/Analytics/StudentAnalyticsDetail.jsx"));

// Student public pages (landing + parent consent) — separate chunk
const StudentLanding = lazy(() => import(/* webpackChunkName: "student-public" */ "./Pages/Students/StudentLanding.jsx"));
const ParentConsentPage = lazy(() => import(/* webpackChunkName: "student-public" */ "./Pages/Students/ParentConsentPage.jsx"));

// Student chunk
const StudentLogin = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Auth/StudentLogin.jsx"));
const StudentForgotPassword = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Auth/ForgotPassword.jsx"));
const StudentResetPassword = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Auth/ResetPassword.jsx"));
const StudentRequestAccess = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Auth/StudentRequestAccess.jsx"));
const StudentDashboard = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Dashboard/StudentDashboard.jsx"));
const StudentProfile = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Profile/StudentProfile.jsx"));
const AssessmentList = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Assessments/AssessmentList.jsx"));
const StudentClassAssessments = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Assessments/StudentClassAssessments.jsx"));
const StudentClassAssessmentResult = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Assessments/StudentClassAssessmentResult.jsx"));
const AssessmentDetail = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Assessments/AssessmentDetail.jsx"));
const AssessmentCalendar = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Assessments/AssessmentCalendar.jsx"));
const StudentResultsView = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Assessments/StudentResultsView.jsx"));
const SubmitAnswers = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Submission/SubmitAnswers.jsx"));
const SubmissionConfirmation = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Submission/SubmissionConfirmation.jsx"));
const SubmissionHistory = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Submission/SubmissionHistory.jsx"));
const ProgressDashboard = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Progress/ProgressDashboard.jsx"));
const SubjectProgress = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Progress/SubjectProgress.jsx"));
const TopicMastery = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Progress/TopicMastery.jsx"));
const StudyRecommendations = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Progress/StudyRecommendations.jsx"));
const StudyResources = lazy(() => import(/* webpackChunkName: "student" */ "./Pages/Student/Progress/StudyResources.jsx"));

// Public shared report (parents)
const SharedReport = lazy(() => import(/* webpackChunkName: "shared-report" */ "./Pages/ParentReport/SharedReport.jsx"));

// Anonymous question-generation funnel (public, no auth) — own lightweight
// chunk so none of this lands on the perf-critical entry bundle.
const GenerateQuestions = lazy(() => import(/* webpackChunkName: "funnel" */ "./Pages/Funnel/GenerateQuestions.jsx"));

// UPSC evaluation surface (upsc.markszen.com) — own chunk, nothing on the
// entry bundle. The landing is MUI-free; the evaluate page pulls MUI lazily.
const UpscLanding = lazy(() => import(/* webpackChunkName: "upsc" */ "./Pages/Upsc/UpscLanding.jsx"));
const UpscEvaluate = lazy(() => import(/* webpackChunkName: "upsc" */ "./Pages/Upsc/UpscEvaluate.jsx"));
const UpscScanDemo = lazy(() => import(/* webpackChunkName: "upsc" */ "./Pages/Upsc/UpscScanDemo.jsx"));
const UpscAuth = lazy(() => import(/* webpackChunkName: "upsc" */ "./Pages/Upsc/UpscAuth.jsx"));
const UpscDashboard = lazy(() => import(/* webpackChunkName: "upsc" */ "./Pages/Upsc/UpscDashboard.jsx"));
const UpscHistory = lazy(() => import(/* webpackChunkName: "upsc" */ "./Pages/Upsc/UpscHistory.jsx"));
const UpscRubricAdmin = lazy(() => import(/* webpackChunkName: "upsc-admin" */ "./Pages/Upsc/UpscRubricAdmin.jsx"));
const UpscSharedReport = lazy(() => import(/* webpackChunkName: "upsc" */ "./Pages/Upsc/UpscSharedReport.jsx"));

// A track subdomain (upsc.markszen.com, college.markszen.com, …) serves its
// vertical experience at '/'; path routes (/upsc, /upsc/evaluate) work on every
// host for dev and deep links. Track selection is config-driven (trackResolver).
const CURRENT_TRACK = getCurrentTrackSlug();
const IS_UPSC_HOST = CURRENT_TRACK === 'upsc';

// A /en/ or /ta/ deep link mounts the router under that prefix so the existing
// routes match unchanged; the bare root ('/') is Hindi (= SEO x-default).
const { basename: LANG_BASENAME } = resolveLangFromUrl();

// Landing-style public surfaces where the first-visit language nudge may show
// (PRD Q2: not on auth pages). Matched by prefix.
const NUDGE_PATHS = ['/students', '/request-demo', '/teacher', '/upsc', '/company', '/generate'];
const isNudgePath = (pathname) =>
  pathname === '/' || NUDGE_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Loads bootstrap.min.css on-demand for non-landing routes only. The marketing
 * landing page (`/`) uses scoped `ul-*` classes exclusively, so loading
 * Bootstrap there is ~28 KiB of pure waste in the CSS bundle. By gating the
 * import on `pathname !== '/'`, Vite emits a separate CSS chunk and the
 * browser fetches it lazily — once cached, navigation between non-landing
 * routes is instant.
 */
function BootstrapLoader() {
  const location = useLocation();
  useEffect(() => {
    // UPSC pages are Bootstrap-free too (scoped uz-* CSS / MUI only).
    if (location.pathname !== '/' && !location.pathname.startsWith('/upsc')) {
      import('bootstrap/dist/css/bootstrap.min.css');
    }
  }, [location.pathname]);
  return null;
}

function RouteFallback() {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#475569",
        fontFamily: "Inter, -apple-system, sans-serif",
        fontSize: 14,
      }}
      role="status"
      aria-live="polite"
    >
      {getLang() === 'hi' ? 'लोड हो रहा है…' : 'Loading…'}
    </div>
  );
}

/**
 * First-visit IP language detection (PRD-MULTILANGUAGE-IP-DETECTION §5.3).
 * Runs once per browser, post-paint, non-blocking. Skipped when the visitor
 * already has a stored choice or arrived via a /{lang}/ URL. On an Indian IP
 * whose detected language isn't English, surfaces the one-time nudge (landing
 * pages only). Keeps i18next out of the entry bundle via dynamic import.
 */
function LanguageDetector() {
  const location = useLocation();
  const [nudgeLang, setNudgeLang] = useState(null);

  useEffect(() => {
    if (getStoredLang() !== null) return; // returning visitor — respect choice
    if (resolveLangFromUrl().urlLang) return; // URL already selected a language
    const controller = new AbortController();
    fetch('/public/detect-language', { signal: controller.signal })
      .then((r) => r.json())
      .then((res) => {
        const data = res?.data;
        if (!data?.language) return;
        setLang(data.language);
        import('./i18n').then((m) => m.default.changeLanguage(data.language));
        if (data.country === 'IN' && data.language !== 'en' && !isNudgeSeen()) {
          setNudgeLang(data.language);
        }
      })
      .catch(() => {
        /* non-blocking: page stays in the default language on any failure */
      });
    return () => controller.abort();
  }, []);

  if (!nudgeLang || !isNudgePath(location.pathname)) return null;
  return <LanguageNudge lang={nudgeLang} onClose={() => setNudgeLang(null)} />;
}

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setUserFromLocalStorage());
    dispatch(setTenantFromLocalStorage());
    dispatch(setStudentFromLocalStorage());
  }, [dispatch]);

  return (
    <BrowserRouter basename={LANG_BASENAME}>
      <BootstrapLoader />
      <ScrollToTop />
      <SessionExpiryListener />
      <GenerationStatusBannerHost />
      <LanguageDetector />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={IS_UPSC_HOST ? <UpscLanding /> : <Homepage />} />
          <Route path="/upsc" element={<UpscLanding />} />
          <Route path="/upsc/evaluate" element={<UpscEvaluate />} />
          <Route path="/upsc/try-paper" element={<UpscScanDemo />} />
          <Route path="/upsc/signup" element={<UpscAuth mode="signup" />} />
          <Route path="/upsc/signin" element={<UpscAuth mode="signin" />} />
          <Route path="/upsc/home" element={<LoginCheck element={<UpscDashboard />} />} />
          <Route path="/upsc/history" element={<LoginCheck element={<UpscHistory />} />} />
          <Route path="/upsc/shared/:token" element={<UpscSharedReport />} />
          <Route path="/students" element={<StudentLanding />} />
          <Route path="/student-consent/:token/:action" element={<ParentConsentPage />} />
          <Route path="/request-demo" element={<RequestDemo />} />
          <Route path="/signin" element={<Auth />} />
          <Route path="/teacher-signup" element={<TeacherRegister />} />
          <Route path="/teacher-signin" element={<TeacherLogin />} />
          <Route path="/teacher-register" element={<RequestAccess />} />
          <Route path="/teacher-login" element={<TeacherLoginPage />} />
          {/* /teachers-login → /teacher-login and /about-us → /company are
              served as HTTP 301s from public/_redirects (Netlify applies them
              before the SPA fallback), so Googlebot follows them as real
              redirects instead of the JS-only <Navigate> routes it flagged as
              "Redirect error". SEO Gap 3. */}
          <Route path="/teacher" element={<IndependentLoginPage />} />
          <Route path="/company" element={<CompanyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/forgotpassword" element={<Forgotpassword />} />
          <Route path="/resetpassword" element={<ResetPassword />} />
          <Route path="/force-password-change" element={<ForcePasswordChange />} />
          <Route path="/myprofile" element={<LoginCheck element={<MyProfileLanding />} />} />
          <Route path="/assessmentnew" element={<LoginCheck element={<CreateAssessment />} />} />
          <Route path="/assessmentupload" element={<LoginCheck element={<UploadAssessment />} />} />
          <Route path="/newheader" element={<LoginCheck element={<AuthHeader />} />} />
          <Route path="/searchfilter" element={<LoginCheck element={<SearchFilter />} />} />
          <Route path="/checkanswer" element={<LoginCheck element={<EvaluateAssessment />} />} />
          <Route path="/rapid-capture" element={<LoginCheck element={<RapidCapture />} />} />
          <Route path="/scan-evaluate" element={<LoginCheck element={<ScanEvaluate />} />} />
          <Route path="/attendance/take" element={<LoginCheck element={<TakeAttendance />} />} />
          <Route path="/review-queue" element={<LoginCheck element={<ReviewQueue />} />} />
          <Route path="/viewassessments" element={<LoginCheck element={<ViewAssessments />} />} />
          <Route path="/viewevaluations" element={<LoginCheck element={<ViewEvaluations />} />} />
          <Route path="/question-analytics" element={<LoginCheck element={<QuestionBankAnalyticsPage />} />} />
          <Route path="/reportcard" element={<LoginCheck element={<EvaluationReport />} />} />
          {/* Public, anonymized sample of the report card (landing hero CTA) — no LoginCheck */}
          <Route path="/sample-report" element={<EvaluationReport sample />} />
          <Route path="/confirmquestions" element={<LoginCheck element={<ConfirmQuestions />} />} />
          <Route path="/confirmanswers" element={<LoginCheck element={<ConfirmAnswers />} />} />
          <Route path="/confirmpaper" element={<LoginCheck element={<ConfirmAnswersheet />} />} />
          <Route path="/confirmanswersheet" element={<LoginCheck element={<ConfirmAnswersheet />} />} />
          <Route path="/viewpaper" element={<LoginCheck element={<ViewAnswers />} />} />
          <Route path="/viewresult" element={<LoginCheck element={<ViewResult />} />} />
          <Route path="/rubrics" element={<LoginCheck element={<RubricManager />} />} />
          <Route path="/review-evaluations" element={<LoginCheck element={<TeacherReview />} />} />
          <Route path="/profile" element={<LoginCheck element={<TeacherProfile />} />} />
          <Route path="/feedback" element={<LoginCheck element={<div className="container py-5"><h2>Feedback</h2><p>Coming soon...</p></div>} />} />
          <Route path="/online-assessments" element={<LoginCheck element={<OnlineAssessmentsDashboard />} />} />
          <Route path="/online-assessments/:id/analytics" element={<LoginCheck element={<AssessmentAnalyticsPage />} />} />

          {/* Super Admin Routes */}
          <Route path="/admin/tenants" element={<SuperAdminCheck element={<TenantManagement />} />} />
          <Route path="/admin/leads" element={<SuperAdminCheck element={<LeadsManagement />} />} />
          <Route path="/admin/evaluations" element={<SuperAdminCheck element={<EvaluationOverview />} />} />
          <Route path="/admin/evaluations/:tenantId/paper/:assessmentId" element={<SuperAdminCheck element={<AdminQuestionPaperView />} />} />
          <Route path="/admin/evaluations/:tenantId/sheet/:evaluationId" element={<SuperAdminCheck element={<AdminAnswerSheetView />} />} />
          <Route path="/admin/diagram-gaps" element={<SuperAdminCheck element={<DiagramGapsDashboard />} />} />
          <Route path="/admin/upsc-rubrics" element={<SuperAdminCheck element={<UpscRubricAdmin />} />} />
          <Route path="/admin/access" element={<OwnerCheck element={<AdminAccess />} />} />
          <Route path="/admin/logs" element={<SuperAdminCheck element={<LogViewer />} />} />
          <Route path="/admin/extraction-lab" element={<SuperAdminCheck element={<ExtractionLab />} />} />
          <Route path="/admin/model-mappings" element={<SuperAdminCheck element={<ModelMappings />} />} />
          <Route path="/admin/prompts" element={<SuperAdminCheck element={<PromptManager />} />} />
          <Route path="/admin/prompts/commits" element={<SuperAdminCheck element={<PromptCommitsPage />} />} />
          <Route path="/admin/suggested-topics" element={<SuperAdminCheck element={<SuggestedTopicsQueue />} />} />
          <Route path="/admin/student-registrations" element={<SuperAdminCheck element={<StudentRegistrationsQueue />} />} />

          {/* Institute Admin Routes */}
          <Route path="/admin/teachers" element={<InstituteAdminCheck element={<TeacherManagement />} />} />
          <Route path="/admin/students/class-wise" element={<InstituteAdminCheck element={<ClassWiseStudentDashboard />} />} />
          <Route path="/admin/students/face-enrollment" element={<InstituteAdminCheck element={<FaceEnrollment />} />} />
          <Route path="/admin/profile-requests" element={<InstituteAdminCheck element={<ProfileChangeRequests />} />} />
          <Route path="/admin/courses" element={<InstituteAdminCheck element={<CourseCatalogue />} />} />
          <Route path="/admin/principal" element={<InstituteAdminCheck element={<PrincipalDashboard />} />} />
          <Route path="/admin/principal/teachers" element={<InstituteAdminCheck element={<TeacherDrilldown />} />} />
          <Route path="/admin/principal/teachers/:teacherId" element={<InstituteAdminCheck element={<TeacherDetailView />} />} />
          <Route path="/admin/principal/analytics/class-performance" element={<InstituteAdminCheck element={<ClassPerformance />} />} />
          <Route path="/admin/principal/analytics/subject-breakdown" element={<InstituteAdminCheck element={<SubjectBreakdown />} />} />
          <Route path="/admin/principal/analytics/teacher-activity" element={<InstituteAdminCheck element={<TeacherActivity />} />} />
          {/* Student analytics — admin OR teacher (backend scopes); LoginCheck like /question-analytics */}
          <Route path="/admin/principal/analytics/students" element={<LoginCheck element={<StudentsAnalytics />} />} />
          <Route path="/admin/principal/analytics/students/:studentId" element={<LoginCheck element={<StudentAnalyticsDetail />} />} />

          {/* Redirects for consolidated pages */}
          <Route path="/admin/users" element={<Navigate to="/admin/teachers" replace />} />
          <Route path="/admin/students" element={<Navigate to="/admin/students/class-wise" replace />} />

          {/* Public Invitation Routes */}
          <Route path="/accept-invite" element={<AcceptInvite />} />

          {/* Student Public Routes */}
          <Route path="/student-login" element={<Navigate to="/student/login" replace />} />
          <Route path="/student/login" element={<StudentLogin />} />
          <Route path="/student/forgot-password" element={<StudentForgotPassword />} />
          <Route path="/student/reset-password" element={<StudentResetPassword />} />
          <Route path="/student/request-access" element={<StudentRequestAccess />} />

          {/* Student Protected Routes */}
          <Route path="/student/dashboard" element={<StudentLoginCheck element={<StudentDashboard />} />} />
          <Route path="/student/profile" element={<StudentLoginCheck element={<StudentProfile />} />} />
          <Route path="/student/assessments" element={<StudentLoginCheck element={<AssessmentList />} />} />
          <Route path="/student/online-assessments" element={<StudentLoginCheck element={<StudentClassAssessments />} />} />
          <Route path="/student/online-assessments/:attemptId/result" element={<StudentLoginCheck element={<StudentClassAssessmentResult />} />} />
          <Route path="/student/assessments/calendar" element={<StudentLoginCheck element={<AssessmentCalendar />} />} />
          <Route path="/student/assessments/:id" element={<StudentLoginCheck element={<AssessmentDetail />} />} />
          <Route path="/student/results/:evaluationId" element={<StudentLoginCheck element={<StudentResultsView />} />} />

          {/* Student Submission Routes */}
          <Route path="/student/assessments/:assessmentId/submit" element={<StudentLoginCheck element={<SubmitAnswers />} />} />
          <Route path="/student/submissions/:submissionId/confirmation" element={<StudentLoginCheck element={<SubmissionConfirmation />} />} />
          <Route path="/student/submissions" element={<StudentLoginCheck element={<SubmissionHistory />} />} />

          {/* Student Progress Tracking Routes */}
          <Route path="/student/progress" element={<StudentLoginCheck element={<ProgressDashboard />} />} />
          <Route path="/student/progress/subjects/:subject" element={<StudentLoginCheck element={<SubjectProgress />} />} />
          <Route path="/student/progress/topics" element={<StudentLoginCheck element={<TopicMastery />} />} />
          <Route path="/student/progress/recommendations" element={<StudentLoginCheck element={<StudyRecommendations />} />} />
          <Route path="/student/resources" element={<StudentLoginCheck element={<StudyResources />} />} />

          {/* Parent Report Public Route (No Authentication Required) */}
          <Route path="/report/:token" element={<SharedReport />} />

          {/* Anonymous Generation Funnel (Public — No Authentication Required) */}
          <Route path="/generate" element={<GenerateQuestions />} />

          {/* Teacher Quiz Routes (Authenticated) */}
          <Route path="/quiz/create" element={<LoginCheck element={<CreateQuiz />} />} />
          <Route path="/quiz/finalized/:assessmentId" element={<LoginCheck element={<QuizFinalized />} />} />

          {/* Online Assessment Public Routes (No Authentication Required) */}
          <Route path="/quiz/join/:linkCode" element={<ParticipantEntryPage />} />
          <Route path="/quiz/join" element={<ParticipantEntryPage />} />
          <Route path="/quiz/take" element={<ParticipantQuizPage />} />
          <Route path="/quiz/result/:attemptId" element={<QuizResultPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
